import { Account } from '@bonanza-social/account';
import { AsyncStorage } from '@bonanza-social/toolkit/storage';
import { DAY_IN_MILLISECONDS } from '@bonanza-social/toolkit/utils';
import { OnboardingStep } from './onboarding.types';

const SECURITY_REVIEW_DISPLAY_TIMESTAMP = 'SECURITY_REVIEW_DISPLAY_TIMESTAMP';

const isApproved = ({ status }: Partial<Account>) => status === 'Approved';

const isPending = ({ status, statusReason }: Partial<Account>) =>
  status === 'Pending' && statusReason === 'KYC incomplete';

const helpers = {
  isWithinAMLAssessment: ({ statusReason }: Partial<Account>) =>
    statusReason === 'Within AML Assessment',

  isUnderSecurityReview: ({ statusReason }: Partial<Account>) =>
    statusReason === 'Under Security Review' ||
    statusReason === 'Restricted for Security Review',

  isVerified: (data: Partial<Account>) => {
    const { status, statusReason } = data;

    return isApproved({ status }) || !isPending({ status, statusReason });
  },

  isVerificationInProgress: ({ status, statusReason }: Partial<Account>) =>
    (status === 'Pending' && statusReason === 'KYC in progress') ||
    (status === 'Pending' && statusReason === 'Manual Verification Required'),

  isManualVerification: (data: Partial<Account>) => {
    const { status, statusReason, verificationMeta } = data;
    const { autoKycAttemptsLeft = 0, maximumAutoKYCAttempts = 0 } =
      verificationMeta || {};

    return (
      (status === 'Pending' &&
        statusReason === 'Document Verification Failed') ||
      (autoKycAttemptsLeft === 0 && maximumAutoKYCAttempts !== 0)
    );
  },

  isRetryVerification: ({
    status,
    statusReason,
    verificationMeta,
  }: Partial<Account>) => {
    const { autoKycAttemptsLeft = 0, maximumAutoKYCAttempts = 0 } =
      verificationMeta || {};

    return (
      isPending({ status, statusReason }) &&
      autoKycAttemptsLeft > 0 &&
      autoKycAttemptsLeft < maximumAutoKYCAttempts
    );
  },

  isReassessment: (data: Partial<Account>) => {
    const { status, statusReason } = data;

    return isPending({ status, statusReason });
  },

  isPendingVerification: ({ status, statusReason }: Partial<Account>) =>
    isPending({ status, statusReason }),
};

export const getOnboardingStep = async ({
  status,
  statusReason,
  verificationMeta,
}: Pick<Account, 'status' | 'statusReason' | 'verificationMeta'> & {}) => {
  if (helpers.isWithinAMLAssessment({ statusReason })) {
    return;
  }

  if (helpers.isUnderSecurityReview({ statusReason })) {
    const lastDisplayTimestamp = await AsyncStorage.getItem(
      SECURITY_REVIEW_DISPLAY_TIMESTAMP,
    );
    const nextDisplayTimestamp =
      lastDisplayTimestamp &&
      Number(lastDisplayTimestamp) + DAY_IN_MILLISECONDS;

    if (
      statusReason === 'Under Security Review' &&
      nextDisplayTimestamp &&
      nextDisplayTimestamp > +new Date()
    ) {
      return;
    }

    AsyncStorage.setItem(SECURITY_REVIEW_DISPLAY_TIMESTAMP, `${+new Date()}`);

    const { autoKycAttemptsLeft = 0, maximumAutoKYCAttempts = 0 } =
      verificationMeta || {};

    return autoKycAttemptsLeft === 0 && maximumAutoKYCAttempts !== 0
      ? OnboardingStep.SECURITY_REVIEW_MANUAL
      : OnboardingStep.SECURITY_REVIEW;
  }

  if (helpers.isVerified({ status, statusReason })) {
    return;
  }

  if (helpers.isVerificationInProgress({ status, statusReason })) {
    return OnboardingStep.VERIFICATION_IN_PROGRESS;
  }

  if (
    helpers.isManualVerification({ status, statusReason, verificationMeta })
  ) {
    return OnboardingStep.MANUAL_VERIFICATION;
  }

  if (helpers.isRetryVerification({ status, statusReason, verificationMeta })) {
    return OnboardingStep.RETRY_VERIFICATION;
  }

  // KYC reassessment
  if (helpers.isReassessment({ status, statusReason })) {
    return OnboardingStep.REASSESSMENT;
  }

  if (helpers.isPendingVerification({ status, statusReason })) {
    return OnboardingStep.PENDING_VERIFICATION;
  }
};
